import { blocksClient } from '@/lib/blocks/client';
import {
  GetNotificationsParams,
  GetNotificationsResponse,
  MarkAllNotificationAsReadResponse,
  MarkNotificationAsReadResponse,
} from '../types/notification.types';

/**
 * The signed-in user's notification inbox, backed by Blocks' `/logic/v4/Notifier/*` endpoints
 * through `blocksClient.notifier`. The legacy `/communication/v1/Notifier/*` routes no longer
 * exist on the Blocks API.
 */

/** IAM answers 2xx on success; only an explicit `isSuccess: false` marks a soft failure. */
const isSuccessful = (res: { isSuccess?: boolean } | undefined): boolean =>
  res?.isSuccess !== false;

/**
 * Fetches paginated notifications with optional filters
 * @param context - React Query context with queryKey array
 * @param context.queryKey.1 - Filtering and pagination params
 * @returns Promise with notifications array and total count
 * @example
 * // Basic usage
 * useQuery({
 *   queryKey: ['notifications', { Page: 1, PageSize: 10 }],
 *   queryFn: getNotifications
 * });
 */

export const getNotifications = async (context: {
  queryKey: [string, GetNotificationsParams];
}): Promise<GetNotificationsResponse> => {
  const [, params] = context.queryKey;

  const res = await blocksClient.notifier.getNotifications({
    filter: params.Filter,
    isUnreadOnly: params.IsUnreadOnly,
    page: params.Page,
    pageSize: params.PageSize,
    sortBy: params['Sort.Property'],
    sortDescending: params['Sort.IsDescending'],
  });

  return res as unknown as GetNotificationsResponse;
};

export const markNotificationAsRead = async (
  id: string
): Promise<MarkNotificationAsReadResponse> => {
  const res = await blocksClient.notifier.markNotificationAsRead({ id });
  return { isSuccess: isSuccessful(res) };
};

export const markAllNotificationAsRead = async (): Promise<MarkAllNotificationAsReadResponse> => {
  const res = await blocksClient.notifier.markAllNotificationAsRead();
  return { isSuccess: isSuccessful(res) };
};
